import React from 'react'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import { toast } from 'react-toastify'
import { ApiPushSendTest } from '../../apiService/Core/Apicall'
import DarkDialog from './DarkDialog'
import { InlineAlert } from './PushDisabledNotice'
import StatusPill from './StatusPill'
import UserPicker from './UserPicker'
import { tableSx, tdStyle, thStyle } from './pushStyles'
import { getErrorMessage, TEST_USERS_MAX } from './pushUtils'
import type { UserLite } from './types'

interface Props {
    open: boolean
    onClose: () => void
    title: string
    body: string
    destination?: string
}

const TestSendDialog: React.FC<Props> = ({ open, onClose, title, body, destination }) => {
    const [users, setUsers] = React.useState<UserLite[]>([])
    const [results, setResults] = React.useState<any[] | null>(null)
    const [error, setError] = React.useState<string | null>(null)
    const [busy, setBusy] = React.useState(false)
    const busyRef = React.useRef(false)

    React.useEffect(() => {
        if (open) {
            setResults(null)
            setError(null)
        }
    }, [open])

    const handleSend = async () => {
        if (busyRef.current) return
        busyRef.current = true
        setBusy(true)
        setError(null)
        try {
            const res = await ApiPushSendTest({
                title,
                body,
                ...(destination ? { destination } : {}),
                userIds: users.map((u) => u.id),
            })
            setResults(res?.data?.results ?? [])
            toast.success('Test notification processed')
        } catch (e) {
            setError(getErrorMessage(e))
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }

    return (
        <DarkDialog
            open={open}
            onClose={onClose}
            title="Send test notification"
            icon={<ScienceOutlinedIcon sx={{ fontSize: 18 }} />}
            busy={busy}
            cancelText="Close"
            confirmText="Send test"
            onConfirm={handleSend}
            confirmDisabled={users.length === 0}
        >
            <Typography sx={{ color: '#9a9a9a', fontSize: 13, mb: 2 }}>
                Sends &quot;{title}&quot; to up to {TEST_USERS_MAX} selected users. Test sends are recorded separately from campaigns.
            </Typography>
            <UserPicker selected={users} onChange={setUsers} max={TEST_USERS_MAX} />
            {error && <Box sx={{ mt: 2 }}><InlineAlert severity="error">{error}</InlineAlert></Box>}
            {results && (
                <Table size="small" sx={{ ...tableSx, mt: 2 }}>
                    <TableHead>
                        <TableRow>
                            {['User', 'Status', 'Reason'].map((h) => <TableCell key={h} sx={thStyle}>{h}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.map((r, i) => (
                            <TableRow key={r.userId ?? i}>
                                <TableCell sx={tdStyle}>{r.name || r.email || r.userId}</TableCell>
                                <TableCell sx={tdStyle}><StatusPill status={r.status} /></TableCell>
                                <TableCell sx={tdStyle}>{r.failureReason ?? '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </DarkDialog>
    )
}

export default TestSendDialog
